// src/context/CallSessionContext.jsx
import React, { createContext, useContext, useState, useMemo } from 'react';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { enrollmentService } from '../services/enrollmentService';
import { useAuth } from './AuthContext';
import Logger from '../utils/logger';
import { createBaseContext } from '../hooks/useBaseContext';

const { Context: CallSessionContext, useContext: useCallSessionContext } = createBaseContext('CallSession');

export const CallSessionProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedCallSession, setSelectedCallSession] = useState(null);
  const [patientCallSessions, setPatientCallSessions] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const { error, loading, handleAsync, clearError } = useErrorHandler();

  // Unified function to handle call session detail opening with a single fetch
  const openCallSessionDetails = async (patient, callSessionId = null, enrollment = null) => {
    // Open modal immediately and clear previous content so the spinner shows
    setShowDetails(true);
    setSelectedCallSession(null);
    setPatientCallSessions([]);
  
    return handleAsync(async () => {
      Logger.log('Opening call session details', { 
        patientId: patient?.id, 
        callSessionId, 
        enrollmentId: enrollment?.id 
      });
  
      if (!enrollment?.id || !enrollment?.medical_center_id) {
        throw new Error('Enrollment context is required to open call session details');
      }
  
      
      const fullEnrollmentData = await enrollmentService.getEnrollmentWithDetails(
        enrollment.id,
        enrollment.medical_center_id,
        user?.role
      );
  
      const sessions = Array.isArray(fullEnrollmentData.call_sessions)
        ? [...fullEnrollmentData.call_sessions]
        : [];
  
      if (sessions.length === 0) {
        Logger.log('No call sessions available for this enrollment');
        setSelectedCallSession(null);
        setPatientCallSessions([]);
        return; // showDetails already true; spinner will stop when loading=false
      }
  
      // Sort newest first
      const sortedSessions = sessions.sort(
        (a, b) => new Date(b.call_date) - new Date(a.call_date)
      );
  
      // Use provided patient data for the modal
      const patientData = patient;
  
      // Attach patient to each session
      const callSessionsWithCompleteData = sortedSessions.map(session => ({
        ...session,
        patient: patientData
      }));
  
      setPatientCallSessions(callSessionsWithCompleteData);
  
      // Select specific session if provided; else latest
      if (callSessionId) {
        const specificSession = sortedSessions.find(cs => cs.id === callSessionId);
        setSelectedCallSession(
          specificSession
            ? { ...specificSession, patient: patientData }
            : { ...sortedSessions[0], patient: patientData }
        );
      } else {
        setSelectedCallSession({ ...sortedSessions[0], patient: patientData });
      }
  
      Logger.log('Call session details opened successfully', { 
        sessionCount: callSessionsWithCompleteData.length 
      });
    });
  };

  const selectCallSession = (callSessionId) => {
    if (!callSessionId) {
      Logger.warn('No call session ID provided for selection');
      return;
    }
    
    const callSession = patientCallSessions.find(cs => cs.id === callSessionId);
    if (callSession) {
      Logger.log('Call session selected', { 
        callSessionId, 
        hasAudio: !!callSession.audio_transcript 
      });
      setSelectedCallSession(callSession);
    } else {
      Logger.warn('Call session not found', { 
        callSessionId, 
        availableIds: patientCallSessions.map(cs => cs.id) 
      });
    }
  };

  const closeDetails = () => {
    Logger.log('Closing call session details');
    setShowDetails(false);
    setSelectedCallSession(null);
    setPatientCallSessions([]);
    clearError();
  };

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    selectedCallSession,
    patientCallSessions,
    showDetails,
    loading,
    error,
    openCallSessionDetails,
    selectCallSession,
    closeDetails
  }), [selectedCallSession, patientCallSessions, showDetails, loading, error, openCallSessionDetails, selectCallSession, closeDetails]);

  return (
    <CallSessionContext.Provider value={contextValue}>
      {children}
    </CallSessionContext.Provider>
  );
};
export { useCallSessionContext };